import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { StarFilled } from '@ant-design/icons';
import { Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import MyTicketsContext from '@totem/components/ticketing/myTickets/myTicketsContext';
import StatusDisplayExtended from '@totem/components/ticketing/statusDisplayExtended';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import TagDisplay, {
  TICKET_PRIORITY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDate, isNotNull } from '@totem/utilities/common';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
import {
  getPriorityFilter,
  getStatusFilter,
  getTicketTypeFilter,
} from '@totem/utilities/ticketing';

const MyTicketsTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { myTicketData, myQueues, loading } = useContext(MyTicketsContext);
  const { handleTableChange, input, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  const queueIndex = (queueId: string) => {
    return myQueues.findIndex((aq) => aq.id === queueId);
  };

  const getInvolvement = (ticket: TicketQueryResult) => {
    const partiesInvolved = ticket.ticket.partiesInvolved.filter(
      (pi) => pi.email === userProfile.email && pi.status === 'ACTIVE',
    );

    const queues = ticket.ticket.queueAssignments.filter(
      (qa) => qa.status === 'ACTIVE' && queueIndex(qa.queueId) >= 0,
    );

    return (
      <>
        {partiesInvolved.map((pi) => (
          <div key={ticket.ticket.id + pi.id}>{pi.type}</div>
        ))}
        {queues.map((qu) => (
          <div key={ticket.ticket.id + qu.id}>{qu.name}</div>
        ))}
      </>
    );
  };

  const columns: ColumnProps<TicketQueryResult>[] = [
    {
      title: 'Number',
      dataIndex: 'number',
      key: 'type',
      render: (_, ticket: TicketQueryResult) => {
        let foundIdx = -1;
        let suffix = <div />;

        if (isNotNull(ticket) && isNotNull(ticket.ticket.favorites)) {
          foundIdx = ticket.ticket.favorites.findIndex(
            (chk) => chk === userProfile.id,
          );
        }

        if (foundIdx >= 0) {
          suffix = (
            <div>
              <StarFilled style={{ color: '#FFC107' }} />
            </div>
          );
        }

        return (
          <Flex justify="space-between" align="center">
            <div>
              <Link to={`/dashboard/ticket/${ticket.ticket.id}`}>
                {ticket.ticket.number}
              </Link>
            </div>
            {suffix}
          </Flex>
        );
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.number === compB.ticket.number
          ? 0
          : compA.ticket.number > compB.ticket.number
            ? 1
            : -1,
      filters: getTicketTypeFilter(),
      filterDropdownProps: {
        placement: 'bottom',
      },
      filteredValue: stringifyArray(getFilterValues('type')),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.category,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.category === compB.ticket.category
          ? 0
          : compA.ticket.category > compB.ticket.category
            ? 1
            : -1,
      filteredValue: stringifyArray(getFilterValues('category')),
      filters: getFilterOptions('category'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'Organization',
      dataIndex: 'organization.name',
      key: 'organization',
      render: (_, ticket: TicketQueryResult) => ticket.organization.name,
      filteredValue: stringifyArray(getFilterValues('organization')),
      filters: getFilterOptions('organization'),
      filterDropdownProps: {
        placement: 'bottom',
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, ticket: TicketQueryResult) => ticket.building.name,
      filteredValue: stringifyArray(getFilterValues('building')),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Summary',
      dataIndex: 'summary',
      key: 'summary',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.summary === compB.ticket.summary
          ? 0
          : compA.ticket.summary > compB.ticket.summary
            ? 1
            : -1,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (_, ticket: TicketQueryResult) => (
        <TagDisplay
          value={ticket.ticket.priority}
          config={TICKET_PRIORITY_STYLES}
        />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.priority === compB.ticket.priority
          ? 0
          : compA.ticket.priority > compB.ticket.priority
            ? 1
            : -1,
      filteredValue: stringifyArray(getFilterValues('priority')),
      filters: getPriorityFilter(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, ticket: TicketQueryResult) => (
        <StatusDisplayExtended ticket={ticket.ticket} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.status === compB.ticket.status
          ? 0
          : compA.ticket.status > compB.ticket.status
            ? 1
            : -1,
      filteredValue: stringifyArray(getFilterValues('status')),
      filters: getStatusFilter(),
    },
    {
      title: 'Involvement',
      dataIndex: 'involvement',
      key: 'involvement',
      render: (_, ticket: TicketQueryResult) => getInvolvement(ticket),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.createdAt),
      sorter: (compA, compB) =>
        compA.ticket.createdAt === compB.ticket.createdAt
          ? 0
          : compA.ticket.createdAt > compB.ticket.createdAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.updatedAt),
      sorter: (compA, compB) =>
        compA.ticket.updatedAt === compB.ticket.updatedAt
          ? 0
          : compA.ticket.updatedAt > compB.ticket.updatedAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
  ];

  const getTickets = () => {
    if (myTicketData !== null) {
      if (myTicketData.tickets !== null) {
        return myTicketData.tickets;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getTickets()}
      loading={loading}
      onChange={handleTableChange}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: isNotNull(myTicketData) ? myTicketData.paging.totalRecords : 0,
        showSizeChanger: true,
      }}
      rowKey={(record) => record.ticket.id}
    />
  );
};

export default MyTicketsTable;
